/**
 * The read-only section the Contest Overview's tab strip switches to — `.vw-section`
 * and its head in `shared/modals.css` (CAE-1573).
 *
 * The Overview tab is built from `OverviewCard`: bordered white panels with an
 * eyebrow over a title. The other four tabs are deliberately not. They are plain
 * sections on the page ground — a title, a counting sub-line, an action to the
 * editor, and a list — because each one *is* the tab, and wrapping a whole tab
 * in a card would draw a box around nothing.
 *
 * Four sections render this shape (Fixtures, Reels, Prizes, Entries), which is
 * why it is in the kit rather than beside the first of them.
 */
import { useId, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

/**
 * The marker the head hangs its link styling off — the same device, and for the
 * same reason, as `CARD_ACTION` on the Overview's cards: a bare `<Link>` keeps
 * TanStack's route generics, where `styled(Link)` or `<Box component={Link}>`
 * widens `to` to `string` and stops `params` type-checking. So the action
 * arrives as a node and the caller keeps its types.
 */
export const VIEW_SECTION_ACTION = 'data-view-section-action';

interface ViewSectionProps {
  /** `.vw-section-title` — and the section's accessible name. */
  title: string;
  /**
   * `.vw-section-sub` — the counting line under the title ("7 attached · 19
   * markets · 54 outcomes"). A node, because a caller assembles it from figures
   * that arrive at different times.
   */
  subtitle: ReactNode;
  /** The head's right-hand link — "Edit fixtures →". See `VIEW_SECTION_ACTION`. */
  action?: ReactNode;
  children: ReactNode;
}

export const ViewSection = ({ title, subtitle, action, children }: ViewSectionProps) => {
  const titleId = useId();

  return (
    <Box
      component="section"
      /*
       * Named by its own heading rather than by an `aria-label`: the heading is
       * on screen and says the same word, and a label would shadow it — leaving
       * the section announced one way and read another.
       */
      aria-labelledby={titleId}
      sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',

          // `.card-action` again, applied to whatever link the caller passed in.
          [`& [${VIEW_SECTION_ACTION}]`]: {
            flexShrink: 0,
            fontSize: '13px',
            fontWeight: 500,
            color: theme.tokens.color.accent,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          },
        })}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            /*
             * `h2`: the tab panel sits under the Contest Overview's single `h1`,
             * and nothing between them carries a heading.
             */
            component="h2"
            id={titleId}
            sx={(theme: Theme) => ({
              margin: '0 0 2px',
              fontSize: '16px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: theme.tokens.color.ink,
            })}
          >
            {title}
          </Box>
          <Box
            component="p"
            sx={(theme: Theme) => ({
              margin: 0,
              fontSize: '13px',
              color: theme.tokens.color.ink3,
            })}
          >
            {subtitle}
          </Box>
        </Box>

        {action}
      </Box>

      {children}
    </Box>
  );
};

interface ViewRowProps {
  /** The row's left-hand mark — crests on a fixture, a thumbnail on an outcome. */
  leading?: ReactNode;
  /** `.vw-row-title`. */
  title: ReactNode;
  /** `.vw-row-meta` — the dot-separated line under the title. */
  meta?: ReactNode;
  /** `.vw-row`'s right-hand group: a status pill, a badge, a count. */
  trailing?: ReactNode;
}

/**
 * One line of a `ViewSection`'s list — `.vw-row` in `shared/modals.css`.
 *
 * A `div`, not a button. These sections are read-only: the row is a record of
 * what is attached, and the one thing an operator can do from here is the
 * section head's link to the editor. The hover treatment is the stylesheet's
 * own — `.vw-row` carries it whether or not anything is clickable — so it stays
 * as a sign the list is live, not as a promise the row navigates.
 */
export const ViewRow = ({ leading, title, meta, trailing }: ViewRowProps) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 14px',
      borderRadius: `${theme.tokens.radius.insetRow}px`,
      backgroundColor: theme.tokens.color.surfaceMuted,
      border: theme.tokens.border.default,
      transition: 'background 0.12s, border-color 0.12s',
      '&:hover': {
        backgroundColor: theme.tokens.componentColor.fixtureRowHover,
        borderColor: theme.tokens.componentColor.viewRowHoverBorder,
      },
    })}
  >
    {leading}

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={(theme: Theme) => ({
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '-0.005em',
          lineHeight: 1.25,
          color: theme.tokens.color.ink,
        })}
      >
        {title}
      </Box>
      {meta === undefined ? null : (
        <Box
          sx={(theme: Theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '3px',
            fontSize: '12.5px',
            color: theme.tokens.color.ink3,
          })}
        >
          {meta}
        </Box>
      )}
    </Box>

    {trailing === undefined ? null : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {trailing}
      </Box>
    )}
  </Box>
);

/** `.vw-list` — the gap between rows, and nothing else. */
export const ViewList = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>{children}</Box>
);

/**
 * `.m-dot-sep` — a drawn 3px disc, not the literal middot `FixtureRow` uses.
 *
 * The two are different marks in the handoff and both are in use: the Overview
 * card's lighter meta line sets a real middot in `--ink-4`, while these sections
 * draw a dot. Decorative, so it is hidden rather than read out as punctuation
 * between every pair of facts.
 */
export const ViewMetaDot = () => (
  <Box
    aria-hidden
    component="span"
    sx={(theme: Theme) => ({
      display: 'inline-block',
      flexShrink: 0,
      width: '3px',
      height: '3px',
      borderRadius: '50%',
      backgroundColor: theme.tokens.componentColor.metaSeparator,
    })}
  />
);
